import { useEffect, useRef } from 'react';
import { AuthLayout, Spinner, Stack, Surface, Text } from '../ui';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../lib/api';
import { useWorkspaceId } from '../lib/auth';

/**
 * Google OAuth redirect landing page (`/oauth/google/callback`, issue #53). Google
 * redirects the browser here with `?code&state` after the consent screen. This page:
 *   1. reads `code` + `state` (or an `error` Google may send if the user declined);
 *   2. posts them to the API callback, which verifies the signed state (CSRF),
 *      exchanges the code for offline tokens, and creates/updates the `google`
 *      connection (the tokens NEVER touch the browser);
 *   3. routes back to Connections, handing a one-shot success/error toast through
 *      sessionStorage (read + cleared by ConnectionsPage on mount).
 *
 * It runs INSIDE the authed shell, so the session token is still present after the
 * round-trip. If the user landed here without a token, the shell already redirected
 * them to /login before this component rendered.
 */

/** sessionStorage key ConnectionsPage reads once to show the post-callback toast. */
export const OAUTH_TOAST_KEY = 'ehe.oauth.google.toast';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();
  // StrictMode double-invokes effects in dev; guard so the (single-use) code is only
  // exchanged once.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    const finish = (toast: string): void => {
      try {
        sessionStorage.setItem(OAUTH_TOAST_KEY, toast);
      } catch {
        /* private mode — the connection still landed; we just skip the toast */
      }
      void navigate({ to: '/app/connections' });
    };

    if (oauthError) {
      finish(`Google connect was cancelled or denied (${oauthError}).`);
      return;
    }
    if (!code || !state) {
      finish('Google connect failed: missing authorization code.');
      return;
    }

    void api
      .completeGoogleConnect(workspaceId, code, state)
      .then((view) => finish(`Connected Google account ${view.externalAccount}.`))
      .catch((err) =>
        finish(err instanceof ApiError ? `Google connect failed: ${err.message}` : 'Google connect failed.'),
      );
  }, [navigate, workspaceId]);

  return (
    <AuthLayout maxWidth="28rem" minHeight="60vh">
      <Surface tone="default" bordered radius="lg" elevation="sm" padding="8">
        <Stack gap="3" align="center">
          <Spinner size="lg" />
          <Text tone="secondary">Finishing Google connection…</Text>
        </Stack>
      </Surface>
    </AuthLayout>
  );
}
